// ============================================
// LOGIN CALLBACK ROUTE
// ============================================

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Loader2, AlertCircle } from 'lucide-react';
import { authApi } from '@/services/api';
import { useAuthStore } from '@/store/auth';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

export const Route = createFileRoute('/login-callback' as any)({
  component: LoginCallbackPage,
});

function LoginCallbackPage() {
  const navigate = useNavigate();
  const { login, setTokens, logout } = useAuthStore();
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const refresh = params.get('refresh');

      if (!token || !refresh) {
        setErrorMsg('Kimlik bilgileri eksik.');
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 3000);
        return;
      }

      try {
        // Set temporary tokens so authApi.me() can use them
        setTokens(token, refresh);

        // Fetch user info
        const response = await authApi.me();
        const result = (response as any).data || response;
        const user = (result as any).user || result;

        if (!user) {
          throw new Error('Kullanıcı bilgisi alınamadı.');
        }

        if (user.role !== 'SEAFARER') {
          throw new Error('Bu panele erişim yetkiniz yok. Sadece adaylar giriş yapabilir.');
        }

        // Login successfully
        login(
          {
            id: user.id,
            email: user.email,
            role: 'SEAFARER',
            firstName: user.seafarer?.firstName,
            lastName: user.seafarer?.lastName,
          },
          token,
          refresh
        );
        toast.success('Giriş başarılı!');
        
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      } catch (err: any) {
        logout();
        setErrorMsg(err?.message || 'Giriş işlemi sırasında hata oluştu.');
        setTimeout(() => {
          navigate({ to: '/login' });
        }, 4000);
      }
    };

    handleCallback();
  }, [navigate, login, setTokens, logout]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-maritime-900 to-maritime-700 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-50 mb-6">
          <img src="/favicon.svg" alt="Crew At Sea" className="w-10 h-10" />
        </div>

        {errorMsg ? (
          <div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Giriş Başarısız</h2>
            <p className="text-gray-600 mb-2">{errorMsg}</p>
            <p className="text-sm text-gray-400">Yönlendiriliyorsunuz...</p>
          </div>
        ) : (
          <div>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 mb-4">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Kimlik Doğrulanıyor</h2>
            <p className="text-gray-600">Lütfen bekleyin, aday paneline yönlendiriliyorsunuz...</p>
          </div>
        )}
      </div>
    </div>
  );
}
